import { useState } from 'react'

import SelectFamilyMember from 'employeeDashboard/common/SelectFamilyMember'
import AddFamilyMemberModal from 'employeeDashboard/common/getReimbursed/common/addFamilyMember/AddFamilyMemberModal'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { FamilyMember, FamilyMemberInsurancePolicy, getFamilyMembersApi, saveFamilyMemberInsurancePolicies } from 'core'

import { Col, DefaultDatePickerV2, DefaultInputV2, Modal, Notification, Row } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import { Dependent, DependentToEdit } from '..'

const newDependent = { firstName: '', lastName: '', policyName: '', startDate: null, endDate: null }

const isEditing = (values): values is DependentToEdit => 'id' in values

const schema = Yup.object({
  id: Yup.string(),
  firstName: Yup.string().when('id', {
    is: (id) => !!id,
    then: Yup.string().required('Required'),
  }),
  lastName: Yup.string().when('id', {
    is: (id) => !!id,
    then: Yup.string().required('Required'),
  }),
  policyName: Yup.string().required('Required'),
  familyMemberId: Yup.string().required('Required'),
  startDate: Yup.date().required('Required').nullable(),
  endDate: Yup.date().min(Yup.ref('startDate'), "End date can't be before start date").required('Required').nullable(),
})

type Props = {
  visible: boolean
  initialValues?: DependentToEdit | Dependent
  onCancel: () => void
  onSubmit: () => Promise<FamilyMemberInsurancePolicy[]>
}

const SaveDependentModal = ({ visible, initialValues, onCancel, onSubmit }: Props) => {
  const [addNewFamilyMemberVisible, setAddNewFamilyMemberVisible] = useState<boolean>(false)

  const { sendCommand: sendSaveCoveredDependents, isLoading: isCoveredDependentsSaveLoading } = useCommandRequestV2(
    (values: Dependent) => saveFamilyMemberInsurancePolicies(values),
  )
  const { data: familyMembers, reload: reloadFamilyMembers } = useQueryRequestV2<FamilyMember[]>({
    initialState: [],
    queryFn: () => getFamilyMembersApi(),
  })

  const handleFormSubmit = async (values: Dependent) => {
    const result = await sendSaveCoveredDependents(values)
    if (result.completed) {
      Notification.open({
        message: 'Dependent saved successfully',
        type: 'success',
      })
      onCancel()
      onSubmit()
    }
  }

  return (
    <Formik initialValues={initialValues || newDependent} validationSchema={schema} onSubmit={handleFormSubmit}>
      {({ handleSubmit, isValid, values, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          <Modal
            visible={visible}
            title={(!initialValues ? 'Add' : 'Edit') + ' dependent details'}
            okText="Save"
            okButtonProps={{
              disabled: !isValid,
              onClick: () => handleSubmit(),
              loading: isCoveredDependentsSaveLoading,
            }}
            onCancel={onCancel}
          >
            {isEditing(values) ? (
              <>
                <DefaultInputV2 name="firstName" label="First name" noErrorHeight />
                <DefaultInputV2 name="lastName" label="Last name" noErrorHeight />
              </>
            ) : (
              <div className="mb-16">
                <SelectFamilyMember
                  inputName="familyMemberId"
                  labelStyles="mb-4"
                  canSelectMe={false}
                  showRequiredSign={false}
                  label="Family member"
                  familyMembers={familyMembers}
                  onChange={(value) => {
                    if (value === 'add') {
                      setAddNewFamilyMemberVisible(true)
                    }
                  }}
                />
              </div>
            )}

            <DefaultInputV2 name="policyName" label="Health insurance policy coverage" required noErrorHeight />
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <DefaultDatePickerV2 name="startDate" label="Start date" noErrorHeight />
              </Col>
              <Col span={12}>
                <DefaultDatePickerV2 name="endDate" label="End date" noErrorHeight />
              </Col>
            </Row>
            <AddFamilyMemberModal
              visible={addNewFamilyMemberVisible}
              onCancel={() => setAddNewFamilyMemberVisible(false)}
              afterSuccessAdd={(familyMemberId: string, familyFirstName: string) => {
                reloadFamilyMembers().then(() => {
                  Notification.open({
                    type: 'success',
                    message: `Family member ${familyFirstName} added successfully`,
                  })
                  setAddNewFamilyMemberVisible(false)
                  setFieldValue('familyMemberId', familyMemberId)
                })
              }}
            />
          </Modal>
        </form>
      )}
    </Formik>
  )
}

export default SaveDependentModal
